import type { Locale } from "@/lib/i18n";

export type InterpolationValues = Record<string, string | number>;

const pluralRulesCache = new Map<string, Intl.PluralRules>();
const numberFormatCache = new Map<string, Intl.NumberFormat>();

function getPluralRules(lang: Locale): Intl.PluralRules {
    const cached = pluralRulesCache.get(lang);
    if (cached) {
        return cached;
    }

    const rules = new Intl.PluralRules(lang);
    pluralRulesCache.set(lang, rules);
    return rules;
}

function getNumberFormat(lang: Locale): Intl.NumberFormat {
    const cached = numberFormatCache.get(lang);
    if (cached) {
        return cached;
    }

    const format = new Intl.NumberFormat(lang);
    numberFormatCache.set(lang, format);
    return format;
}

function resolvePluralKey(
    key: string,
    values: InterpolationValues,
    translations: Record<string, unknown>,
    lang: Locale,
): string | null {
    const count = values.count;
    if (typeof count !== "number") {
        return null;
    }

    const category = getPluralRules(lang).select(count);
    const candidates = [`${key}_${category}`, `${key}_other`, key];

    for (const candidate of candidates) {
        const value = translations[candidate];
        if (typeof value === "string") {
            return value;
        }
    }

    return null;
}

function interpolate(
    template: string,
    values: InterpolationValues,
    lang: Locale,
): string {
    const format = getNumberFormat(lang);

    return template.replace(/\{(\w+)\}/g, (_, placeholder: string) => {
        const value = values[placeholder];
        if (value === undefined) {
            return `{${placeholder}}`;
        }

        if (typeof value === "number") {
            return format.format(value);
        }

        return String(value);
    });
}

export function resolveTranslation(
    key: string,
    values: InterpolationValues | undefined,
    translations: Record<string, unknown>,
    lang: Locale,
): string | null {
    if (values && "count" in values) {
        const pluralTemplate = resolvePluralKey(key, values, translations, lang);
        if (pluralTemplate !== null) {
            return interpolate(pluralTemplate, values, lang);
        }
    }

    const raw = translations[key];
    if (typeof raw !== "string") {
        return null;
    }

    if (values && Object.keys(values).length > 0) {
        return interpolate(raw, values, lang);
    }

    return raw;
}
